import { IManifest, ISegment, ISegmentPart } from 'm3u8-parser';

export interface IHydratedPart extends ISegmentPart {
  partNumber: number;
  msnNumber: number;
  startTs?: number;
  endTs?: number;
}

interface IHydratedPreloadHint {
  uri: string;
  type: string;
  msnNumber: number;
  partNumber: number;
}

interface IHydratorOptions {
  partHoldBack?: number;
}

export interface IHydratedSegment extends Omit<ISegment, 'parts'> {
  parts: IHydratedPart[];
  msnNumber: number;
  partNumber: number;
}

export interface IHydratedManifest extends Omit<IManifest, 'segments' | 'preloadSegment'> {
  segments: IHydratedSegment[];
  preloadSegment: IHydratedSegment & {
    preloadHints: IHydratedPreloadHint[];
  };
}

const populatePartsWithTimings: (parts: IHydratedPart[], startTs: number) => IHydratedPart[] = (parts, startTs) =>
  parts.reduce<{ parts: IHydratedPart[]; prevStart: number }>(
    (prevState, currentPart) => {
      currentPart.startTs = prevState.prevStart;
      currentPart.endTs = currentPart.startTs + currentPart.duration;
      prevState.prevStart = currentPart.endTs;
      prevState.parts.push(currentPart);

      return prevState;
    },
    { parts: [], prevStart: startTs },
  ).parts;

export const m3u8Hydrator: (manifest: IManifest, options?: IHydratorOptions) => IHydratedManifest = (manifest, options = {}) => {
  const msn: number = manifest.mediaSequence;
  const segments: IHydratedManifest['segments'] = manifest.segments
    .map((segment, index) => ({
      ...segment,
      uri: segment.uri,
      msnNumber: msn + index,
      partNumber: 0,
      parts: (segment?.parts || []).map((part, indexOfPart) => {
        return {
          ...part,
          msnNumber: msn + index,
          partNumber: indexOfPart,
        };
      }),
    }))
    .map((segment) => {
      segment.parts = segment.dateTimeObject
        ? populatePartsWithTimings(segment.parts, segment.dateTimeObject.getTime() / 1000)
        : segment.parts;
      return segment;
    });

  const lastMSN: number = msn + segments.length;

  const preloadParts: IHydratedManifest['preloadSegment']['parts'] = (manifest.preloadSegment?.parts || []).map((part, index) => ({
    ...part,
    msnNumber: lastMSN,
    partNumber: index,
  }));

  const preloadHints: IHydratedManifest['preloadSegment']['preloadHints'] = (manifest.preloadSegment?.preloadHints || []).map(
    (preloadHint, index) => ({
      ...preloadHint,
      msnNumber: lastMSN,
      partNumber: index + preloadParts.length,
    }),
  );

  const lastSegment: IHydratedSegment = segments.slice(-1)[0];

  const preloadSegment: IHydratedManifest['preloadSegment'] = {
    ...manifest.preloadSegment,
    parts:
      manifest.preloadSegment && lastSegment.dateTimeObject
        ? populatePartsWithTimings(preloadParts, lastSegment.dateTimeObject.getTime() / 1000 + lastSegment.duration)
        : preloadParts,
    preloadHints: preloadHints,
    msnNumber: lastMSN,
    partNumber: 0,
  };

  const hydratedManifest: IHydratedManifest = {
    ...manifest,
    segments,
    preloadSegment,
  };

  if (options.partHoldBack) {
    hydratedManifest.partHoldBack = options.partHoldBack;
  }

  return hydratedManifest;
};
